import { PitchResult } from './pitchDetection';

// Ring buffer of analysis frames (spectrum + pitch) so the tracker can be
// paused, scrubbed, and inspected. Spectra are stored as 8-bit dB so thirty
// seconds of 60 fps frames at 2048 bins is ~3.7 MB instead of 15 MB.

export const STORE_MIN_DB = -100;
export const STORE_MAX_DB = -10;
const DB_SPAN = STORE_MAX_DB - STORE_MIN_DB;

export class FrameStore {
  readonly capacity: number;
  readonly bins: number;
  private specs: Uint8Array;
  private times: Float64Array;
  private hz: Float32Array;
  private clarity: Float32Array;
  private head = 0;   // next physical write slot
  private count = 0;

  constructor(capacity: number, bins: number) {
    this.capacity = capacity;
    this.bins = bins;
    this.specs = new Uint8Array(capacity * bins);
    this.times = new Float64Array(capacity);
    this.hz = new Float32Array(capacity);
    this.clarity = new Float32Array(capacity);
  }

  get length() { return this.count; }

  push(t: number, spectrum: Float32Array, pitch: PitchResult | null) {
    const slot = this.head;
    const base = slot * this.bins;
    const n = Math.min(this.bins, spectrum.length);
    const specs = this.specs;
    for (let i = 0; i < n; i++) {
      const v = (spectrum[i] - STORE_MIN_DB) / DB_SPAN;
      specs[base + i] = v <= 0 ? 0 : v >= 1 ? 255 : (v * 255) | 0;
    }
    this.times[slot] = t;
    this.hz[slot] = pitch ? pitch.hz : 0;
    this.clarity[slot] = pitch ? pitch.clarity : 0;
    this.head = (slot + 1) % this.capacity;
    if (this.count < this.capacity) this.count++;
  }

  /** Physical slot for logical index j, where 0 = oldest and length-1 = newest. */
  private slot(j: number) {
    return (this.head - this.count + j + this.capacity) % this.capacity;
  }

  timeAt(j: number) { return this.times[this.slot(j)]; }
  hzAt(j: number) { return this.hz[this.slot(j)]; }
  clarityAt(j: number) { return this.clarity[this.slot(j)]; }
  get newestTime() { return this.count ? this.times[this.slot(this.count - 1)] : 0; }
  get oldestTime() { return this.count ? this.times[this.slot(0)] : 0; }

  /** Quantized spectrum row (0 = STORE_MIN_DB, 255 = STORE_MAX_DB) for logical index j. */
  rowAt(j: number): Uint8Array {
    const base = this.slot(j) * this.bins;
    return this.specs.subarray(base, base + this.bins);
  }

  /** Expand a stored row back into a dB Float32Array (reuses `out` when sized right). */
  dequantize(j: number, out?: Float32Array): Float32Array {
    const row = this.rowAt(j);
    const o = out && out.length === this.bins ? out : new Float32Array(this.bins);
    for (let i = 0; i < this.bins; i++) o[i] = STORE_MIN_DB + (row[i] / 255) * DB_SPAN;
    return o;
  }

  /** Logical index of the newest frame with time <= t, or -1 if every frame is newer. */
  indexAtTime(t: number): number {
    let lo = 0, hi = this.count - 1, ans = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (this.timeAt(mid) <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1;
    }
    return ans;
  }

  clear() { this.head = 0; this.count = 0; }
}
